import React, { useCallback } from "react";
import { Button, Card, Collapse } from "react-bootstrap";
import {
  Book,
  ClipboardCheck,
  Mortarboard,
  ChevronUp,
  ChevronDown,
  XCircleFill,
} from "react-bootstrap-icons";
import KatexText from "../../KatexText"; // adjust path as needed
import PreviewStatement from "../../Practice/PreviewStatement";
import LazyLoad from "react-lazyload";
import DisplayOptions from "../../../pages/StudentPages/Practice/displayOptions";
import RenderLatexContent from "../../RenderLatexContent";
import { isEmpty } from "lodash";
import { isNumberExact, isNumberLiesInRange, isNumberWithPrecision } from "../../../utils/formater";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {getUserRole} from "../../../store/userRole";

/*
 * Shared row-rendering logic for wrong-question lists, used by BOTH
 * RecentMistakesWidget (dashboard) and WrongQuestionsPage (View All).
 * Extracted so the two never drift out of sync on how a question,
 * its options, and its correct/incorrect state are rendered.
 */

const INK = "#1F2430";
const MUTED = "#6B7280";
const CARD = "#FFFFFF";
const HAIRLINE = "#EBEAF2";
const BRAND = "#6C5DD3";
const BRAND_SOFT = "#F1EEFE";
const WRONG = "#E0546A";
const CORRECT = "#2F9E63";
const BODY_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const EXAM_TYPE_META = {
  practice: { label: "Practice Set", icon: Book, color: "#3B6FE0", bg: "#EAF1FF" },
  test: { label: "Test Set", icon: ClipboardCheck, color: "#B7791F", bg: "#FEF6E7" },
  assessment: { label: "Full Length Exam", icon: Mortarboard, color: BRAND, bg: BRAND_SOFT },
};

const EXAM_IELTS_TYPE_META = {
  ...EXAM_TYPE_META,
  isIELTSExamSource: { label: "Full Length Exam", icon: Mortarboard, color: BRAND, bg: BRAND_SOFT },
}

function getSetName(item) {
  return item.setName || item.practiceSetName || item.testName || item.assessmentName || item.name;
}

const getCorrectAnswerForQuestion = (questionData) => {
    if (!questionData) return "";

    if (questionData.type === "mcq" || questionData.type === "true_false") {
      return String.fromCharCode(questionData?.answer_index + 1 + 64);
    }
    if (questionData.type === "blank") {
      const options = [...questionData?.options];
      return options.join(" , ");
    }
    if (questionData.type === "numerical") {
      const index = questionData?.options?.findIndex(
        (option) => typeof option === "object",
      );
      let option = "";
      if (index !== -1) {
        option = questionData?.options[index];
      } else {
        const stringInd = questionData?.options?.findIndex(
          (option) => typeof option === "string",
        );
        option = {
          type: "Exact Answer:",
          value: stringInd === -1 ? "" : questionData?.options[stringInd],
          errorMargin: "0",
        };
      }
      if (option?.type === "Exact Answer:") {
        return option?.value;
      }
      if (option?.type === "Answer with Precision:") {
        return option?.value;
      }
      if (option?.type === "Answer in the Range:") {
        return `Between ${option?.minValue} and ${option?.maxValue}`;
      }
    }
    if (questionData.type === "multiple_answers") {
      return (questionData?.correct_answers || [])
        .map((value) => {
          return `Option ${value + 1}`;
        })
        .join(", ");
    }
    if (questionData.type === "matching") {
      const options = questionData?.options.map((option) => {
        return `${option?.leftSide} --> ${option?.rightSide}`;
      });
      return options.join(",  ");
    }
    if (questionData.type === "multiple_blanks") {
      const options = questionData?.options.map((option, index) => {
        return `${index + 1} )  ${option?.options
          ?.map((opt) => opt?.option)
          .join(" or ")}`;
      });
      return options.join(" , ");
    }
    if (questionData.type === "multiple_dropdown") {
      const options = questionData?.options.map((option, index) => {
        const result = option.options.filter((opt) => opt.value);
        return `${index + 1} )  ${result[0]?.option}`;
      });
      return options.join(" , ");
    }
    return "";
  };

  const isCorrectAnswer = (question,data) => {
    if (
      isEmpty(data) &&
      !(
        question?.type === "mcq" ||
        question?.type === "true_false"
      )
    ) {
      return false;
    }
    if (
      question?.type === "mcq" ||
      question?.type === "true_false"
    ) {
      return question?.answer_index === data;
    }
    if (question?.type === "blank") {
      const options = [...question?.options].map((option) =>
        option?.toLowerCase(),
      );
      const value = data ? data?.toLowerCase() : "";
      return options.includes(value);
    }
    if (question?.type === "numerical") {
      const index = question?.options?.findIndex(
        (option) => typeof option === "object",
      );
      let option = "";
      if (index !== -1) {
        option = question?.options[index];
      } else {
        const stringInd = question?.options?.findIndex(
          (option) => typeof option === "string",
        );
        option = {
          type: "Exact Answer:",
          value:
            stringInd === -1
              ? ""
              : question?.options[stringInd],
          errorMargin: "0",
        };
      }
      if (option?.type === "Exact Answer:") {
        return isNumberExact(data, question, option);
      }
      if (option?.type === "Answer in the Range:") {
        return isNumberLiesInRange(option?.minValue, option?.maxValue, data);
      }
      if (option?.type === "Answer with Precision:") {
        return isNumberWithPrecision(data, option?.precision, option?.value);
      }
    }
    if (question?.type === "multiple_answers") {
      const answersList = question?.correct_answers || [];
      if (answersList.length !== data.length) {
        return false;
      }
      return data.every((entry) => answersList.includes(entry));
    }
    if (question?.type === "matching") {
      const options = question?.options.map((option) => {
        return option?.rightSide;
      });
      if (options.length !== data?.length) {
        return false;
      }
      return data.every((entry, index) => options[index] === entry);
    }
    if (question?.type === "multiple_blanks") {
      const options = question?.options.map((option) => {
        return option?.options?.map((opt) => opt?.option?.toLowerCase());
      });
      if (options?.length * 2 !== data?.length) {
        return false;
      }
      let check = true;
      options.forEach((option, index) => {
        if (
          !data[index * 2 + 1] ||
          !option.includes(
            data[index * 2 + 1] ? data[index * 2 + 1]?.toLowerCase() : "",
          )
        ) {
          check = false;
        }
      });
      return check;
    }
    if (question?.type === "multiple_dropdown") {
      const options = question?.options.map((option) => {
        return option.options
          .filter((opt) => opt.value)
          .map((opt) => opt.option);
      });
      if (options?.length * 2 !== data?.length) {
        return false;
      }
      let check = true;
      options.forEach((option, index) => {
        if (!data[index * 2 + 1] || !option.includes(data[index * 2 + 1])) {
          check = false;
        }
      });
      return check;
    }
  };

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function SkeletonRow() {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 4px" }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: "#EFEFF4", flexShrink: 0 }} className="rmw-pulse" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ height: 12, width: "70%", borderRadius: 6, background: "#EFEFF4", marginBottom: 8 }} className="rmw-pulse" />
        <div style={{ height: 10, width: "40%", borderRadius: 6, background: "#EFEFF4" }} className="rmw-pulse" />
      </div>
    </div>
  );
}

function TypeBadge({ examType, isIELTSExamSource = false }) {
  const meta = EXAM_IELTS_TYPE_META[examType] || EXAM_IELTS_TYPE_META.practice;
  const Icon = meta.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 999,
        background: meta.bg,
        color: meta.color,
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      <Icon size={12} />
      {meta.label}
    </span>
  );
}

function WrongQuestionRow({ item, isOpen, onToggle, isMobileView = false, subjectMap= {}, hasIeltsExams = false }) {
  const roleOfTheUser = useSelector(getUserRole);
  const navigate = useNavigate()
  const { id: classroomId } = useParams();
  const isAssessment = item.examType === "assessment";
  const isIELTSExamSource = item?.examSource === 'IELTS';
  const statement = item.question?.statement;
  const setName = getSetName(item);
  const ChevronIcon = isOpen ? ChevronUp : ChevronDown;

  const studentAnswerText =
    typeof item.studentAnswer === "object" && item.studentAnswer !== null
      ? JSON.stringify(item.studentAnswer)
      : item.studentAnswer;

  const getCorrectAnswerIndex = useCallback((question) => {
    return question?.options.indexOf(question?.correct_answers);
  }, []);

  const getUserAnswerIndex = useCallback((question, userAnswer) => {
    return question?.options.indexOf(userAnswer);
  }, []);

  let correctAnswerIndex = getCorrectAnswerIndex(item?.question);
  if (correctAnswerIndex === -1 && item?.question?.type !== "numerical") {
    correctAnswerIndex = item?.question?.answer_index;
  }

  const answered = studentAnswerText;
  const userAnswerIndex = answered
    ? getUserAnswerIndex(item?.question, answered)
    : -1;
  const userAttempted = userAnswerIndex !== -1;

  return (
    <div>
      <div
        onClick={onToggle}
        className="wrong-questions"
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "14px 4px",
          borderRadius: 12,
          cursor: "pointer",
          transition: "background 0.15s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAFC")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              color: INK,
              lineHeight: 1.45,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {statement ? <KatexText text={statement} /> : <span style={{ color: MUTED }}>Untitled question</span>}
          </div>

          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
            <TypeBadge examType={isIELTSExamSource ? 'isIELTSExamSource' : item.examType} />
            {setName && <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>{setName}</span>}
          </div>

          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 5, fontSize: 11.5 }}>
            {!isAssessment && !isIELTSExamSource && subjectMap[item?.subjectId] && (
              <span style={{ color: MUTED }}>{subjectMap[item.subjectId]}</span>
            )}
            {!isAssessment && !isIELTSExamSource && item.topicName && (
              <>
                <span style={{ color: "#D8D8E2" }}>•</span>
                <span style={{ color: MUTED }}>{item.topicName}</span>
              </>
            )}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: WRONG, marginLeft: 2 }}>
              <XCircleFill size={11} />
              Incorrect
            </span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>
            {timeAgo(isAssessment ? item.attemptCreatedAt : item.answeredAt || item.attemptCreatedAt)}
          </span>
          <ChevronIcon size={15} color={MUTED} />
        </div>
      </div>

      <Collapse in={isOpen}>
        <div style={{
          backgroundColor: 'whitesmoke',
          padding: '12px'
        }}>
          <div style={{ padding: "2px 4px 16px 4px" }}>
            {item?.examType === 'test' || item?.examType === 'practice' || item?.examSource === 'IELTS' ? 
              <>
                <PreviewStatement
                    data={item.question}
                    disableListGroup
                    className={isMobileView ? "py-1" : "py-2"}
                    userAnswer={
                        item?.studentAnswer
                    }
                    updateUserAnswer={() => {}}
                    isAttempted
                />

                {item.question?.questionImageUrl && (
                    <div className="d-flex justify-content-center">
                        <LazyLoad height={20} offset={100}>
                        <img
                            alt="question"
                            src={item.question?.questionImageUrl}
                            style={{
                            width: "70%",
                            maxWidth: "100%",
                            height: "auto",
                            display: "block",
                            }}
                        />
                        </LazyLoad>
                    </div>
                )}

                <DisplayOptions
                    data={item?.question}
                    userAnswer={item?.studentAnswer}
                    updateUserAnswer={() =>{}}
                    isAttempted
                    isMobileView={isMobileView}
                    isCorrectAnswert={(data) => {
                      const value = isCorrectAnswer(item?.question, data)
                      return value
                    }}
                />
                
                <div
                  className="d-flex justify-content-end"
                  style={{ marginTop: isMobileView ? "8px" : "12px" }}
                >
                  <div
                    style={{
                      padding: isMobileView
                        ? "4px 8px"
                        : "8px 16px",
                      borderRadius: "4px",
                      backgroundColor: item?.question?.isCorrect
                        ? "#E8F5E9"
                        : "#FFEBEE",
                      color: item?.question?.isCorrect
                        ? "#4CAF50"
                        : "#F44336",
                      fontSize: isMobileView ? "9px" : "14px",
                      fontWeight: 500,
                    }}
                  >
                    {item?.question?.isCorrect
                      ? "✓ Correct"
                      : "✗ Incorrect"}
                  </div>
                </div>
                <div
                  style={{
                    marginTop: isMobileView ? "8px" : "12px",
                    paddingTop: isMobileView ? "8px" : "12px",
                    borderTop: "1px solid #E5E7EB",
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobileView ? "11px" : "14px",
                      fontWeight: 600,
                      color: "#39393B",
                      marginBottom: isMobileView ? "4px" : "6px",
                    }}
                  >
                    Correct Answer:{" "}
                    {getCorrectAnswerForQuestion(item.question)}
                  </div>
                  <div
                    style={{
                      fontSize: isMobileView ? "10px" : "14px",
                      fontWeight: 400,
                      lineHeight: isMobileView ? "16px" : "22px",
                      color: "#4E5566",
                    }}
                  >
                    <RenderLatexContent
                      content={item.question?.explanation}
                    />
                  </div>
                  {item.question?.answerImageUrl && (
                    <div className="d-flex justify-content-center mt-3">
                      <LazyLoad height={20} offset={100}>
                        <img
                          alt="answer"
                          src={item.question?.answerImageUrl}
                          style={{
                            width: "70%",
                            maxWidth: "100%",
                            height: "auto",
                            display: "block",
                          }}
                        />
                      </LazyLoad>
                    </div>
                  )}
                </div>
              </> : 
              <>
                <Card
                  id={`${item?.question?._id}`}
                  className={`mb-3 incorrect detail-summary`}
                >
                  <Card.Body>
                    <Card.Text>
                      {/* <FormattedText text={question?.statement} /> */}
                      <RenderLatexContent content={item?.question?.statement} />
                    </Card.Text>
                    <b>
                      <Card.Text>
                        {item?.question?.type === "numerical" ? "Answer:" : "Options:"}
                      </Card.Text>
                    </b>
                    <ul className="list-unstyled">
                      {(item?.question?.type === "numerical"
                        ? [item?.question?.options[0]]
                        : item?.question?.options
                      )?.map((option, optionIndex) => {
                        const optionText =
                          item?.question?.type === "numerical"
                            ? answered?.length
                              ? answered
                              : option.value
                            : option;
                        return (
                          <div
                            key={optionIndex}
                            className="d-flex align-items-center gap-2"
                          >
                            {item?.question?.type !== "numerical" && (
                              <b>{String.fromCharCode(65 + optionIndex)}</b>
                            )}{" "}
                            {item?.question?.type === "numerical" ? (
                              <li
                                key={optionIndex}
                                className={`w-100 option-item-result incorrect-numerical-result`}
                              >
                                {/* Render the option content based on the option type */}
                                <RenderLatexContent content={optionText} />
                              </li>
                            ) : (
                              <li
                                key={optionIndex}
                                className={`w-100 option-item-result ${
                                  optionIndex === correctAnswerIndex
                                    ? "correct-result"
                                    : ""
                                } ${
                                  userAttempted && optionIndex === userAnswerIndex
                                    ? optionIndex === correctAnswerIndex
                                      ? ""
                                      : "incorrect-result"
                                    : ""
                                }`}
                              >
                                {/* Render the option content based on the option type */}
                                <RenderLatexContent content={optionText} />
                              </li>
                            )}
                          </div>
                        );
                      })}
                    </ul>
                    <Card.Text>
                      Explanation:
                      <div
                        style={{
                          backgroundColor: "#feedaf",
                          color: "black",
                          padding: "20px",
                          border: "2px solid black",
                          borderRadius: "8px",
                        }}
                      >
                        {/* <FormattedText text={question?.explanation} /> */}
                        <RenderLatexContent content={item?.question?.explanation} />
                      </div>
                    </Card.Text>
                  </Card.Body>
                </Card>
              </>
            }
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>
              Attempted {formatDateTime(isAssessment ? item.attemptCreatedAt : item.answeredAt || item.attemptCreatedAt)}
              {hasIeltsExams && (
              <div><Button size="sm" style={{marginTop: '10px'}} onClick={()=>{

                const assessmentURL = `${roleOfTheUser === "Student" ? "/student" : ""}/myClassrooms/${classroomId}/ielts-exams/${item?.examId}/attempt/${item?.sectionType}/results?fromFullExam=true&attemptId=${item?.attemptId}&questionId=${item.question?._id}`
                const attemptURL = `${roleOfTheUser === "Student" ? "/student" : ""}/myClassrooms/${classroomId}/${item?.subjectId}/${item?.topicId}/${item?.examType}/${item?.attemptId}/${item?.examId}`
                navigate(item?.examType === 'assessment' ? assessmentURL : attemptURL)
              }}>View Complete Details</Button></div>
              )}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
}


export {
  INK,
  MUTED,
  CARD,
  HAIRLINE,
  BRAND,
  BRAND_SOFT,
  WRONG,
  CORRECT,
  BODY_FONT,
  EXAM_TYPE_META,
  getSetName,
  timeAgo,
  formatDateTime,
  SkeletonRow,
  TypeBadge,
  WrongQuestionRow,
};